/** venture/LpItemsEditor.tsx — LP 후보 「항목 편집」 (2026-11-01 지도사 「링크 복사 눌렀는데 자동으로 만들어졌는데 편집이 여기서 바로 되면 좋겠네 — 가이드 링크 · 설명글 등이 빠진 게 있어서」)
 *  · 조합 자료수집 카드와 같은 편집 부품(TemplateBar · ItemList) — LP 개인자료 + 회사 서류 · 항목 제목 · 유형 · 설명 · 가이드 링크/파일 · 필수 · 민감 · 순서
 *  · 처음 열면 지금 포털 항목(없으면 기본 항목) · 「저장」 → 포털이 있으면 바로 반영(제출물이 달린 항목은 남김), 없으면 이 항목으로 만든다
 *  · 조합에 매칭되면 이 항목 그대로 조합 LP 카드로 옮겨진다(Venture.tsx lpIntoProject 에 불러온 항목을 넘김) */
import React, { useEffect, useState } from 'react';
import Icon from './icons';
import ItemList, { DragSrc } from './ItemList';
import TemplateBar from './TemplateBar';
import { lpCollect, collectFromPortal } from './lpMove';
import { getCard, portalItems } from './collect';
import { getLpPortalItems, saveLpPortalItems } from './api';
import type { Lp } from './api';
import type { Collect } from './types';

interface Props { lp: Lp; onClose: () => void; onSaved: (pid: string, total: number) => void; toast: (m: string) => void }

export default function LpItemsEditor({ lp, onClose, onSaved, toast }: Props) {
  const [state, setState] = useState<{ c: Collect; ref: string } | null>(null);
  const [pid, setPid] = useState('');
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragSrc, setDragSrc] = useState<DragSrc | null>(null);

  useEffect(() => {
    let alive = true;
    getLpPortalItems(lp.id)
      .then((r) => { if (!alive) return; setPid(r.collect_pid || ''); setState(collectFromPortal(lp, r.items)); })
      .catch((e) => { if (!alive) return; toast('지금 항목을 불러오지 못해 기본 항목으로 열었어요 — ' + ((e as Error).message || '')); setState(lpCollect(lp)); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lp.id]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const edit = (fn: (c: Collect) => void) => setState((s) => {
    if (!s) return s;
    const c = JSON.parse(JSON.stringify(s.c)) as Collect;
    fn(c); setDirty(true);
    return { c, ref: s.ref };
  });

  const save = async () => {
    if (!state || busy) return;
    const items = portalItems(state.c, state.ref);
    if (!items.length) { toast('항목을 하나 이상 남겨 주세요'); return; }
    setBusy(true);
    try {
      const r = await saveLpPortalItems(lp.id, items, 'roum');
      onSaved(r.collect_pid, r.total);
      toast((r.created ? '받는 링크를 만들고 항목을 저장했어요' : '항목을 저장했어요 — 받는 화면에 바로 반영돼요') + (r.kept ? ` · 이미 제출된 항목 ${r.kept}건은 남겨 뒀어요` : ''));
      onClose();
    } catch (e) { toast('⚠ 저장 실패 — ' + ((e as Error).message || '')); } finally { setBusy(false); }
  };

  const card = state ? getCard(state.c, state.ref) : null;
  return (
    <div className="ovm" onClick={onClose}>
      <div className="ovbox" style={{ width: 'min(880px, 96vw)', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div className="ovh"><b>✏️ {lp.company} · 받을 자료 편집</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>
        <div style={{ padding: '14px 16px', overflowY: 'auto', flex: 1 }}>
          <div className="snote" style={{ marginBottom: 10 }}>
            {pid ? '받는 링크가 이미 있어요 — 저장하면 받는 화면에 바로 반영돼요(이미 제출된 항목은 지우지 않아요).' : '아직 받는 링크가 없어요 — 저장하면 이 항목으로 링크를 만들어요.'} 항목을 누르면 설명 · 가이드 링크 · 파일을 적을 수 있어요.
          </div>
          {!state || !card ? <div className="snote">불러오는 중…</div> : (
            <>
              <TemplateBar refId={state.ref} card={card} edit={edit} toast={toast} kind="lp" trackDefault={false} label="LP 개인자료 템플릿" />
              <div className="lbl">LP 개인 자료</div>
              <ItemList refId={state.ref} items={card.items} scope="person" sent={!!pid} edit={edit} dragSrc={dragSrc} setDragSrc={setDragSrc} />
              {state.c.cos.map((co) => (
                <React.Fragment key={co.id}>
                  <div className="mergesec" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ flex: 1, minWidth: 0 }}>🏢 회사 서류 — {co.name || '(회사명)'}</span>
                  </div>
                  <TemplateBar refId={'co:' + co.id} card={co} edit={edit} toast={toast} kind="co" trackDefault={false} label="회사 서류 템플릿" />
                  <ItemList refId={'co:' + co.id} items={co.items} scope="co" sent={!!pid} edit={edit} dragSrc={dragSrc} setDragSrc={setDragSrc} />
                </React.Fragment>
              ))}
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
          <button className="rbtn" style={{ minWidth: 120, height: 34 }} onClick={onClose}>{dirty ? '저장 안 하고 닫기' : '닫기'}</button>
          <button className="btn pri sm" style={{ minWidth: 120, height: 34, justifyContent: 'center' }} disabled={!state || busy} onClick={() => void save()}>{busy ? '저장 중…' : pid ? '저장 · 바로 반영' : '저장 · 링크 만들기'}</button>
        </div>
      </div>
    </div>
  );
}
